/**
 * @file footer.component.tsx
 * @path apps/website/src/components/layout/footer.component.tsx
 * @description Branded footer using the Sir Bastians Design System.
 */

import {
  LEGAL_NAV_ITEMS,
  MAIN_NAV_ITEMS,
  PATHS,
  type NavItem,
} from "@/config/paths.config";
import { branding, siteMetadata, socialLinks } from "@/config/site.config";
import { ArrowUpRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

// --- Sub-Components ---

/**
 * Renders a column of internal navigation links with Montserrat headings.
 */
const FooterLinkColumn = ({
  title,
  links,
  onLinkClick,
}: {
  title: string;
  links: NavItem[];
  onLinkClick: (e: React.MouseEvent<HTMLAnchorElement>, path: string) => void;
}) => (
  <div className="space-y-6">
    <h2 className="text-sm mr-auto font-bold font-montserrat text-nav-text tracking-widest uppercase">
      {title}
    </h2>
    <ul className="space-y-4">
      {links.map((item) => (
        <li key={item.name}>
          {/* ✨ UPDATED: Added onClick to handle smooth scrolling for internal footer links */}
          <Link
            to={item.path}
            onClick={(e) => onLinkClick(e, item.path)}
            aria-label={`Maps to ${item.name}`}
            className="text-sm font-open-sans text-muted-foreground transition-all duration-300 hover:text-accent hover:translate-x-1 inline-block"
          >
            {item.name}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

/**
 * Renders external social media connections using brand accents.
 */
const SocialLinks = () => (
  <div className="space-y-6">
    <h2 className="text-sm font-bold font-montserrat text-nav-text tracking-widest uppercase">
      Connect
    </h2>
    <ul className="space-y-4">
      {socialLinks.map((link) => (
        <li key={link.label}>
          <a
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Visit our ${link.label} page`}
            className="inline-flex items-center gap-1.5 text-sm font-medium font-open-sans text-muted-foreground transition-colors hover:text-accent group"
          >
            <ArrowUpRight
              className="h-4 w-4 text-accent transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
              aria-hidden="true"
            />
            {link.label}
          </a>
        </li>
      ))}
    </ul>
  </div>
);

// --- Main Component ---

export const Footer = () => {
  // ✨ NEW: Get current path
  const { pathname } = useLocation();

  const companyLinks = MAIN_NAV_ITEMS.filter(
    (item) => item.path !== PATHS.ROOT,
  );

  // ✨ NEW: Reusable handler to check if we are on the target page.
  const handleSamePageScroll = (
    e: React.MouseEvent<HTMLAnchorElement>,
    targetPath: string,
  ) => {
    if (pathname === targetPath) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <footer className="bg-nav-bg text-nav-text">
      <div className="px-6 py-16 sm:py-24 lg:px-14">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-2 md:mr-0 lg:grid-cols-7 lg:gap-0 ">
          {/* Brand and Description */}
          <div className="md:col-span-2 lg:col-span-4">
            {/* ✨ UPDATED: Added onClick to handle smooth scrolling for the logo */}
            <Link
              to={PATHS.ROOT}
              onClick={(e) => handleSamePageScroll(e, PATHS.ROOT)}
              className="flex items-center justify-start gap-4 group"
            >
              <div className="flex w-full items-center gap-3 overflow-hidden px-1">
                {/* Logo Container with Neuro-Bezel */}
                <div className="relative flex aspect-square size-16 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground neuro-bezel">
                  <img
                    src={branding.logoLight}
                    alt={siteMetadata.title}
                    className="size-full object-cover rounded-xl"
                  />
                </div>

                {/* Brand Text using Nav Theme */}
                <div className="flex flex-1 flex-col justify-center space-y-2">
                  <span className="truncate text-3xl font-bold tracking-widest leading-none text-nav-text">
                    {branding.title}
                  </span>
                  <span className="truncate text-lg font-bold uppercase leading-none tracking-[0.2em] text-nav-text-muted">
                    {branding.subtitle}
                  </span>
                </div>
              </div>
            </Link>
            <p className="mt-8 max-w-xs text-sm leading-relaxed text-muted-foreground">
              {siteMetadata.description}
            </p>
          </div>

          {/* Legal Column */}
          <div className="lg:col-start-5">
            <FooterLinkColumn
              title="Legal"
              links={LEGAL_NAV_ITEMS}
              onLinkClick={handleSamePageScroll}
            />
          </div>

          {/* Company Column */}
          <div className="md:col-start-1 lg:col-start-6">
            <FooterLinkColumn
              title="Company"
              links={companyLinks}
              onLinkClick={handleSamePageScroll}
            />
          </div>

          {/* Social Column */}
          <div className="lg:col-start-7">
            <SocialLinks />
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-20 flex flex-col items-center justify-between gap-6 border-t border-border pt-10 sm:flex-row">
          <p className="text-xs text-muted-foreground font-medium">
            &copy; {new Date().getFullYear()} {siteMetadata.title}. All rights
            reserved.
          </p>
          <p className="text-xs text-muted-foreground">
            Proudly Developed by{" "}
            <a
              href="https://idksolutions.org"
              target="_blank"
              rel="noopener"
              className="font-bold text-accent hover:underline transition-all"
            >
              IDK Solutions
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};
